/**
 * Friends Component
 * Display friends list, pending requests, and send friend request feature
 */

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Friends.css';

const Friends = ({ userId, onMessageClick, openSignal = 0, hideTrigger = false }) => {
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showSearchUser, setShowSearchUser] = useState(false);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState({});

  // Fetch friends and pending requests
  useEffect(() => {
    if (userId) {
      fetchFriends();
      fetchPendingRequests();
      fetchOnlineStatus();
      // Refresh online status every 5 seconds
      const interval = setInterval(fetchOnlineStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  useEffect(() => {
    if (openSignal) {
      setShowFriendsList(true);
    }
  }, [openSignal]);

  const fetchFriends = async () => {
    try {
      const res = await api.get('/friends');
      console.log('👥 Friends fetched:', res.data);
      setFriends(res.data);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    }
  };

  const fetchOnlineStatus = async () => {
    try {
      const res = await api.get('/friends/online-status');
      console.log('🟢 Friends online status response:', res.data);
      // Create a map of friend_id -> is_online
      const statusMap = {};
      res.data.forEach(friend => {
        statusMap[friend.friend_id] = friend.is_online;
        console.log(`  ${friend.friend_username} (ID: ${friend.friend_id}): ${friend.is_online ? 'ONLINE' : 'OFFLINE'}`);
      });
      console.log('Status map:', statusMap);
      setFriendsOnlineStatus(statusMap);
    } catch (error) {
      console.error('Failed to fetch online status:', error);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      setLoadingRequests(true);
      const res = await api.get('/friends/pending');
      setPendingRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSearchUser = async (e) => {
    e.preventDefault();
    const trimmedUsername = searchUsername.trim();
    if (!trimmedUsername) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchError('');
      console.log(`🔍 Searching for: "${trimmedUsername}"`);
      const res = await api.get('/users/search', {
        params: { username: trimmedUsername }
      });
      console.log('Search results:', res.data);
      if (res.data && res.data.length > 0) {
        setSearchResults(res.data);
      } else {
        setSearchError('No users found with that username');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('❌ Search error details:', error.message);
      console.error('Error response:', error.response?.data);
      setSearchError(error.response?.data?.error || 'Error searching for user');
      setSearchResults([]);
    }
  };

  const handleSendRequest = async (targetUserId) => {
    try {
      console.log(`📤 Sending friend request to user ${targetUserId}`);
      await api.post(`/friends/request/${targetUserId}`);
      setSearchUsername('');
      setSearchResults([]);
      setShowSearchUser(false);
      await fetchPendingRequests();
      alert('Friend request sent!');
    } catch (error) {
      console.error('❌ Error sending request:', error.response?.data);
      const errorMsg = error.response?.data?.error || 'Failed to send friend request';
      alert(errorMsg);
    }
  };

  const handleAcceptRequest = async (friendshipId) => {
    try {
      await api.post(`/friends/accept/${friendshipId}`);
      await fetchFriends();
      await fetchPendingRequests();
      alert('Friend request accepted!');
    } catch (error) {
      alert('Failed to accept friend request');
    }
  };

  const handleDeclineRequest = async (friendshipId) => {
    try {
      await api.post(`/friends/decline/${friendshipId}`);
      await fetchPendingRequests();
      alert('Friend request declined');
    } catch (error) {
      alert('Failed to decline friend request');
    }
  };

  const handleCancelSentRequest = async (friendshipId) => {
    try {
      await api.post(`/friends/cancel/${friendshipId}`);
      await fetchPendingRequests();
      alert('Friend request cancelled');
    } catch (error) {
      alert(error?.response?.data?.error || 'Failed to cancel request');
    }
  };

  const handleRemoveFriend = async (friendshipId) => {
    if (window.confirm('Remove this friend?')) {
      try {
        await api.delete(`/friends/${friendshipId}`);
        await fetchFriends();
        alert('Friend removed');
      } catch (error) {
        alert('Failed to remove friend');
      }
    }
  };

  // Count received requests
  const receivedRequests = pendingRequests.filter(r => r.direction === 'received');
  const sentRequests = pendingRequests.filter(r => r.direction === 'sent');

  return (
    <div className="friends-container">
      {!hideTrigger && (
        <button 
          className="friends-toggle-btn"
          onClick={() => setShowFriendsList(!showFriendsList)}
          aria-label="Friends"
        >
          <span className="friends-toggle-icon" aria-hidden="true">👥</span>
          <span className="friends-toggle-text">Friends</span>
          {friends.length > 0 && <span className="badge">{friends.length}</span>}
          {receivedRequests.length > 0 && <span className="badge pending">{receivedRequests.length}</span>}
        </button>
      )}

      {/* Friends Panel */}
      {showFriendsList && (
        <>
          <div
            className="menu-backdrop"
            onClick={() => setShowFriendsList(false)}
            aria-hidden="true"
          />
          <div className="friends-panel">
            <div className="friends-header">
              <h3>Friends & Requests</h3>
              <button 
                className="close-btn"
                onClick={() => setShowFriendsList(false)}
              >
                ✕
              </button>
            </div>

          {/* Pending Requests Section */}
          <div className="requests-section">
            <h4>Friend Requests ({receivedRequests.length})</h4>
            {loadingRequests ? (
              <p className="loading">Loading...</p>
            ) : receivedRequests.length === 0 ? (
              <p className="empty">No pending requests</p>
            ) : (
              <div className="requests-list">
                {receivedRequests.map((req) => (
                  <div key={req.id} className="request-item">
                    <div className="request-info">
                      <p className="username">{req.requester_username}</p>
                      <p className="date">{new Date(req.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="request-actions">
                      <button 
                        className="accept-btn"
                        onClick={() => handleAcceptRequest(req.id)}
                      >
                        ✓ Accept
                      </button>
                      <button 
                        className="decline-btn"
                        onClick={() => handleDeclineRequest(req.id)}
                      >
                        ✕ Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sent Requests Section */}
          {sentRequests.length > 0 && (
            <div className="sent-requests-section">
              <h4>Sent Requests ({sentRequests.length})</h4>
              <div className="requests-list">
                {sentRequests.map((req) => (
                  <div key={req.id} className="sent-request-item">
                    <div className="sent-request-info">
                      <p className="username">{req.other_user_username}</p>
                      <p className="status">Pending...</p>
                    </div>
                    <div className="sent-request-actions">
                      <button
                        className="cancel-request-btn"
                        onClick={() => handleCancelSentRequest(req.id)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Friends List */}
          <div className="friends-list-section">
            <h4>Friends ({friends.length})</h4>
            {friends.length === 0 ? (
              <p className="empty">No friends yet</p>
            ) : (
              <div className="friends-list">
                {friends.map((friend) => (
                  <div key={friend.id} className="friend-item">
                    <div className="friend-info">
                      <div className="friend-name-with-status">
                        <p className="username">{friend.friend_username}</p>
                        <div className={`online-indicator ${friendsOnlineStatus[friend.friend_id] ? 'online' : 'offline'}`}
                             title={friendsOnlineStatus[friend.friend_id] ? 'Online' : 'Offline'}>
                        </div>
                      </div>
                      <p className="stats">
                        🏆 {friend.friend_wins} wins
                      </p>
                    </div>
                    <div className="friend-actions">
                      <button 
                        className="message-btn"
                        onClick={() => onMessageClick && onMessageClick(friend)}
                        title="Send message"
                      >
                        💬 Message
                      </button>
                      <button 
                        className="remove-btn"
                        onClick={() => handleRemoveFriend(friend.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Friend Button */}
          <div className="add-friend-section">
            <button 
              className="add-friend-btn"
              onClick={() => setShowSearchUser(!showSearchUser)}
            >
              + Add Friend
            </button>

            {showSearchUser && (
              <div className="search-user-form">
                <form onSubmit={handleSearchUser}>
                  <input
                    type="text"
                    placeholder="Search username..."
                    value={searchUsername}
                    onChange={(e) => setSearchUsername(e.target.value)}
                  />
                  <button type="submit">Search</button>
                </form>

                {searchError && <p className="error">{searchError}</p>}

                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map((user) => (
                      <div key={user.id} className="search-result-item">
                        <p className="username">{user.username}</p>
                        <button 
                          className="send-request-btn"
                          onClick={() => handleSendRequest(user.id)}
                        >
                          Send Request
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        </>
      )}

    </div>
  );
};

export default Friends;
